import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cleanTrackingParams } from '../lib/urlUtils';
import { encryptSecret, decryptSecret, generatePassword } from '../lib/vaultCrypto';
import { inferSmartTags } from '../lib/tagClassifier';
import { parseCredentialRecord, formatCredentialRecord } from '../lib/credentialParser';

const {
  FiX, FiTrash2, FiSave, FiLink, FiType, FiFolder,
  FiFileText, FiLock, FiEye, FiEyeOff, FiKey, FiShield, FiUser, FiZap
} = FiIcons;

const EMPTY_FORM = {
  title: '', url: '', type: 'link',
  icon: '', logoUrl: '', parentId: 'root',
  note: '', credentials: '',
};

const EditModal = ({
  isOpen, onClose, item, onSave, onDelete, isNew,
  themeColors = {
    accent: '#c2ffe0', text: '#e5e5e5', muted: '#666666',
    card: 'rgba(26,26,26,0.85)', border: 'rgba(255,255,255,0.08)',
    surface: 'rgba(255,255,255,0.04)',
  },
  userId = 'default_user',
  isDark: isDarkProp,
}) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [credUser, setCredUser] = useState('');
  const [credPass, setCredPass] = useState('');
  const [credCode, setCredCode] = useState('');
  const [isFetchingLogo, setIsFetchingLogo] = useState(false);
  const [showCreds, setShowCreds] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const prevIsOpen = useRef(false);

  const isDark = isDarkProp !== undefined
    ? Boolean(isDarkProp)
    : !themeColors?.card?.toLowerCase().includes('ffffff') && !themeColors?.card?.startsWith('rgba(255');
  const inputBg = isDark ? 'rgba(0,0,0,0.35)' : '#FFFFFF';
  const inputBorder = isDark ? themeColors.border : '#CBD5E1';

  useEffect(() => {
    let cancelled = false;
    if (isOpen && !prevIsOpen.current) {
      setActiveTab('basic');
      setShowCreds(false);
      if (isNew || !item) {
        setFormData(EMPTY_FORM);
        setCredUser('');
        setCredPass('');
        setCredCode('');
      } else {
        setFormData({
          title: item?.title || '',
          url: item?.url || '',
          type: item?.type || 'link',
          icon: item?.icon || '',
          logoUrl: item?.logoUrl || '',
          parentId: item?.parentId || 'root',
          note: item?.note || '',
          credentials: '',
        });
        setCredUser('');
        setCredPass('');
        setCredCode('');
        if (item?.credentials) {
          decryptSecret(item.credentials, userId).then((dec) => {
            if (!cancelled) {
              setFormData((prev) => ({ ...prev, credentials: dec }));
              const parsed = parseCredentialRecord(dec);
              setCredUser(parsed.username || parsed.email || '');
              setCredPass(parsed.password || '');
              setCredCode(parsed.activationCode || '');
            }
          });
        }
      }
    }
    prevIsOpen.current = isOpen;
    return () => { cancelled = true; };
  }, [isOpen, isNew, item, userId]);

  useEffect(() => {
    if (formData.type === 'link' && formData.url) fetchLogo(formData.url);
  }, [formData.url, formData.type]);

  const fetchLogo = async (url) => {
    try {
      setIsFetchingLogo(true);
      const domain = new URL(url).hostname;
      setFormData(prev => ({ ...prev, logoUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=128` }));
    } catch (_) {}
    finally { setIsFetchingLogo(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanedUrl = formData.type === 'link' ? cleanTrackingParams(formData.url.trim()) : '';
    const formatted = formatCredentialRecord({
      username: credUser.trim(),
      password: credPass.trim(),
      activationCode: credCode.trim()
    });
    let finalCreds = formatted || formData.credentials.trim();
    if (finalCreds) {
      finalCreds = await encryptSecret(finalCreds, userId);
    }
    onSave({
      ...formData,
      url: cleanedUrl || formData.url,
      credentials: finalCreds,
    });
  };

  const handleTypeChange = (newType) => {
    setFormData(prev => ({
      ...prev, type: newType,
      url: newType === 'folder' ? '' : prev.url,
      logoUrl: newType === 'folder' ? '' : prev.logoUrl,
      note: newType === 'folder' ? '' : prev.note,
      credentials: newType === 'folder' ? '' : prev.credentials,
    }));
    if (newType === 'folder') setActiveTab('basic');
  };

  const set = (key) => (e) => setFormData(prev => ({ ...prev, [key]: e.target.value }));

  const inputStyle = { backgroundColor: inputBg, borderColor: inputBorder, color: themeColors.text };
  const focusStyle = (e) => (e.currentTarget.style.borderColor = themeColors.accent);
  const blurStyle = (e) => (e.currentTarget.style.borderColor = inputBorder);

  const isLink = formData.type === 'link';
  const tabs = [
    { id: 'basic', label: 'Basic' },
    ...(isLink ? [{ id: 'note', label: 'Note' }, { id: 'credentials', label: 'Credentials' }] : []),
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="border rounded-2xl shadow-2xl w-full max-w-md relative pointer-events-auto overflow-hidden"
              style={{ backgroundColor: themeColors.card, borderColor: themeColors.border }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-5 pb-4">
                <h2 className="text-xl font-semibold" style={{ color: themeColors.text }}>
                  {isNew ? 'Add New Item' : 'Edit Item'}
                </h2>
                <button onClick={onClose} style={{ color: themeColors.muted }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = themeColors.text)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = themeColors.muted)}>
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs — only show if link */}
              {isLink && (
                <div className="flex gap-1 px-6 mb-4">
                  {tabs.map((t) => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={activeTab === t.id
                        ? { backgroundColor: `${themeColors.accent}22`, color: themeColors.accent, border: `1px solid ${themeColors.accent}55` }
                        : { color: themeColors.muted, border: '1px solid transparent' }
                      }>
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="px-6 pb-2 space-y-4" style={{ minHeight: 220 }}>
                  <AnimatePresence mode="wait">

                    {/* BASIC TAB */}
                    {activeTab === 'basic' && (
                      <motion.div key="basic" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                        className="space-y-4">
                        {/* Type selector */}
                        <div>
                          <label className="block text-xs font-medium mb-2" style={{ color: themeColors.muted }}>Type</label>
                          <div className="flex gap-3">
                            {[
                              { type: 'link', icon: FiLink, label: 'Bookmark' },
                              { type: 'folder', icon: FiFolder, label: 'Folder' },
                            ].map(({ type, icon, label }) => (
                              <button key={type} type="button" onClick={() => handleTypeChange(type)}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all"
                                style={formData.type === type
                                  ? { borderColor: themeColors.accent, backgroundColor: `${themeColors.accent}1a`, color: themeColors.text }
                                  : { borderColor: themeColors.border, color: themeColors.muted, backgroundColor: 'transparent' }
                                }>
                                <SafeIcon icon={icon} className="w-4 h-4" />
                                <span className="text-sm font-medium">{label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Title */}
                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: themeColors.muted }}>Title</label>
                          <div className="relative">
                            <SafeIcon icon={FiType} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: themeColors.muted }} />
                            <input type="text" value={formData.title} onChange={set('title')}
                              className="w-full border rounded-lg px-10 py-2.5 focus:outline-none transition-colors text-sm"
                              style={inputStyle} placeholder="Enter title..." required
                              onFocus={focusStyle} onBlur={blurStyle} />
                          </div>
                        </div>

                        {/* URL */}
                        {isLink && (
                          <div>
                            <div className="flex items-center justify-between mb-1.5">
                              <label className="block text-xs font-medium" style={{ color: themeColors.muted }}>URL</label>
                              {formData.url && formData.url.includes('?') && (
                                <button
                                  type="button"
                                  onClick={() => setFormData((p) => ({ ...p, url: cleanTrackingParams(p.url) }))}
                                  className="text-[10px] text-emerald-400 hover:underline flex items-center gap-1"
                                  title="Strip tracking query parameters"
                                >
                                  <SafeIcon icon={FiShield} className="w-2.5 h-2.5" /> Strip Trackers
                                </button>
                              )}
                            </div>
                            <div className="relative">
                              <SafeIcon icon={FiLink} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: themeColors.muted }} />
                              <input type="url" value={formData.url} onChange={set('url')}
                                className="w-full border rounded-lg px-10 py-2.5 focus:outline-none transition-colors text-sm"
                                style={inputStyle} placeholder="https://example.com" required
                                onFocus={focusStyle} onBlur={blurStyle} />
                              {isFetchingLogo && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                  <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                                    style={{ borderColor: themeColors.accent }} />
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Logo preview */}
                        {isLink && formData.logoUrl && (
                          <div className="flex items-center gap-3 p-3 border rounded-lg"
                            style={{ backgroundColor: inputBg, borderColor: themeColors.border }}>
                            <img src={formData.logoUrl} alt="Logo" className="w-8 h-8 rounded-lg object-contain"
                              onError={(e) => { e.target.style.display = 'none'; }} />
                            <span className="text-xs" style={{ color: themeColors.muted }}>Auto-fetched logo</span>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {/* NOTE TAB */}
                    {activeTab === 'note' && (
                      <motion.div key="note" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} className="space-y-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="flex items-center gap-1.5 text-xs font-medium" style={{ color: themeColors.muted }}>
                            <SafeIcon icon={FiFileText} className="w-3.5 h-3.5" /> Note & Tags
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const suggested = inferSmartTags(formData.url, formData.title);
                              const existing = formData.note ? formData.note.trim() : '';
                              const toAdd = suggested.filter((t) => !existing.includes(t)).join(' ');
                              setFormData((p) => ({ ...p, note: existing ? `${existing} ${toAdd}` : toAdd }));
                            }}
                            className="text-[11px] font-mono px-2 py-0.5 rounded-lg border flex items-center gap-1 text-purple-300 bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 transition-all cursor-pointer"
                            title="Infer tags based on URL and title"
                          >
                            <span>✨ Auto-Tag</span>
                          </button>
                        </div>
                        <textarea
                          value={formData.note}
                          onChange={set('note')}
                          rows={5}
                          placeholder="Add notes or tags (e.g. #dev #reading #ai)..."
                          className="w-full border rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none transition-colors"
                          style={inputStyle}
                          onFocus={focusStyle} onBlur={blurStyle}
                        />
                        <div className="flex items-center flex-wrap gap-1.5 pt-1">
                          <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Quick Tags:</span>
                          {['#dev', '#ai', '#design', '#media', '#finance', '#reading', '#tools'].map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                const existing = formData.note || '';
                                if (!existing.includes(tag)) {
                                  setFormData((p) => ({ ...p, note: `${existing} ${tag}`.trim() }));
                                }
                              }}
                              className={`text-[10px] px-2 py-0.5 rounded-md font-mono transition-colors cursor-pointer border ${
                                isDark
                                  ? 'bg-white/[0.04] hover:bg-white/[0.08] text-white/70 border-white/10'
                                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                              }`}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {/* CREDENTIALS TAB */}
                    {activeTab === 'credentials' && (
                      <motion.div key="credentials" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} className="space-y-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <label className="flex items-center gap-1.5 text-xs font-medium" style={{ color: themeColors.muted }}>
                              <SafeIcon icon={FiLock} className="w-3.5 h-3.5" /> Structured Credentials
                            </label>
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                              <SafeIcon icon={FiShield} className="w-2.5 h-2.5" />
                              AES-256
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                const pwd = generatePassword({ length: 18 });
                                setCredPass(pwd);
                                setShowCreds(true);
                              }}
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border transition-all hover:scale-105"
                              style={{ borderColor: `${themeColors.accent}50`, color: themeColors.accent, backgroundColor: `${themeColors.accent}15` }}
                              title="Generate Strong Password"
                            >
                              <SafeIcon icon={FiKey} className="w-3 h-3" /> Gen Pass
                            </button>
                            <button type="button" onClick={() => setShowCreds(v => !v)}
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg transition-colors"
                              style={{ color: themeColors.muted }}>
                              <SafeIcon icon={showCreds ? FiEyeOff : FiEye} className="w-3 h-3" />
                              {showCreds ? 'Hide' : 'Show'}
                            </button>
                          </div>
                        </div>

                        {/* Email or Username */}
                        <div>
                          <label className="block text-[11px] font-medium mb-1" style={{ color: themeColors.muted }}>Email / Username</label>
                          <div className="relative">
                            <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: themeColors.muted }} />
                            <input
                              type="text"
                              value={credUser}
                              onChange={(e) => setCredUser(e.target.value)}
                              placeholder="user@example.com or username"
                              onFocus={focusStyle} onBlur={blurStyle}
                              className="w-full border rounded-lg pl-9 pr-3 py-2 text-xs font-mono focus:outline-none transition-colors"
                              style={inputStyle}
                            />
                          </div>
                        </div>

                        {/* Password */}
                        <div>
                          <label className="block text-[11px] font-medium mb-1" style={{ color: themeColors.muted }}>Password</label>
                          <div className="relative">
                            <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: themeColors.muted }} />
                            <input
                              type={showCreds ? 'text' : 'password'}
                              value={credPass}
                              onChange={(e) => setCredPass(e.target.value)}
                              placeholder="••••••••••••"
                              onFocus={focusStyle} onBlur={blurStyle}
                              className="w-full border rounded-lg pl-9 pr-3 py-2 text-xs font-mono focus:outline-none transition-colors"
                              style={inputStyle}
                            />
                          </div>
                        </div>

                        {/* Activation Code / 2FA Key */}
                        <div>
                          <label className="block text-[11px] font-medium mb-1" style={{ color: themeColors.muted }}>Activation Code / License / 2FA</label>
                          <div className="relative">
                            <SafeIcon icon={FiZap} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: themeColors.muted }} />
                            <input
                              type="text"
                              value={credCode}
                              onChange={(e) => setCredCode(e.target.value)}
                              placeholder="e.g. ABCD-1234-EFGH or 2FA secret"
                              onFocus={focusStyle} onBlur={blurStyle}
                              className="w-full border rounded-lg pl-9 pr-3 py-2 text-xs font-mono focus:outline-none transition-colors"
                              style={inputStyle}
                            />
                          </div>
                        </div>

                        <p className="text-[10px] pt-1" style={{ color: themeColors.muted }}>
                          Encrypted on your device before saving. Auto-fill will match this domain.
                        </p>
                      </motion.div>
                    )}

                  </AnimatePresence>
                </div>

                {/* Actions */}
                <div className="flex gap-3 px-6 py-4 border-t mt-2" style={{ borderColor: themeColors.border }}>
                  {!isNew && (
                    <button type="button" onClick={() => onDelete(item)}
                      className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg transition-colors text-sm">
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" /> Delete
                    </button>
                  )}
                  <button type="submit"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors font-medium text-sm"
                    style={{ backgroundColor: themeColors.accent, color: '#000000' }}>
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    {isNew ? 'Add Item' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditModal;